import { useEffect, useState } from "react";

// Code from https://usehooks.com/useLocalStorage

export interface Serialization<T> {
  serialize: (item: T) => string;
  deserialize: (item: string) => T;
}

type LocalStorageChangeDetail = {
  key: string;
  value: unknown;
};

type LocalStorageChangeEvent = CustomEvent<LocalStorageChangeDetail>;

export const JSON_SERIALIZATION: Serialization<unknown> = {
  serialize: (item: unknown) => JSON.stringify(item),
  deserialize: (item: string) => JSON.parse(item),
};

export const NUMBER_SERIALIZATION: Serialization<number | null> = {
  serialize: (item: number | null) => item?.toString() || "",
  deserialize: (item: string) => (item ? parseFloat(item) : null),
};

export const STRING_SERIALIZATION: Serialization<string> = {
  serialize: (item: string) => (item === "null" ? "" : item),
  deserialize: (item: string) => (item === "null" ? "" : item),
};

type ReturnType<T> = [T, React.Dispatch<React.SetStateAction<T>>];

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  serialization: Serialization<T> = JSON_SERIALIZATION as Serialization<T>,
): ReturnType<T> {
  const getStoredValue = () => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? serialization.deserialize(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(getStoredValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: React.Dispatch<React.SetStateAction<T>> = (
    value,
  ) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(
        key,
        serialization.serialize(valueToStore),
      );

      const event = new CustomEvent("localStorageChange", {
        detail: { key, value: valueToStore },
      });
      window.dispatchEvent(event);
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  // Listen to storage changes (in case another tab/window changes the value)
  useEffect(() => {
    const listener = (evt: StorageEvent) => {
      if (evt.key !== key) return;
      try {
        const item = window.localStorage.getItem(key);
        setStoredValue(
          item ? serialization.deserialize(item) : initialValue,
        );
      } catch (error) {
        console.log(error);
        setStoredValue(initialValue);
      }
    };

    const localStorageChangeListener = (
      evt: Event | LocalStorageChangeEvent,
    ) => {
      const customEvt = evt as LocalStorageChangeEvent;
      if (customEvt.detail?.key !== key) return;
      try {
        const item = window.localStorage.getItem(key);
        setStoredValue(
          item ? serialization.deserialize(item) : initialValue,
        );
      } catch (error) {
        console.log(error);
        setStoredValue(initialValue);
      }
    };

    addEventListener("storage", listener, false);
    addEventListener(
      "localStorageChange",
      localStorageChangeListener,
    );
    return () => {
      removeEventListener("storage", listener);
      removeEventListener(
        "localStorageChange",
        localStorageChangeListener,
      );
    };
  }, [key, initialValue, serialization]);

  return [storedValue, setValue];
}
